/**
 * Derives every Purchase Dashboard KPI from raw SAP OData entity sets.
 *
 * Kept free of React so the maths can be checked in isolation. Each builder
 * takes the raw rows from services/odata.js and returns { kpis, charts, tables }.
 *
 * Two SAP quirks drive most of the defensive code here:
 *   - item numbers are padded inconsistently between services (see itemKey)
 *   - amounts and quantities arrive as strings, not numbers (see num)
 */
import { parseSapDate, toISODate, num, itemKey, unpadId } from './odata';

// ---------- small helpers ----------

const sum = (rows, pick) => rows.reduce((total, row) => total + num(pick(row)), 0);

const groupBy = (rows, pick) =>
  rows.reduce((acc, row) => {
    const key = pick(row);
    (acc[key] = acc[key] || []).push(row);
    return acc;
  }, {});

const topN = (rows, n) => [...rows].sort((a, b) => b.value - a.value).slice(0, n);

const pct = (part, whole) => (whole ? (part / whole) * 100 : null);

const daysBetween = (later, earlier) =>
  later && earlier ? Math.round((later - earlier) / 86400000) : null;

/**
 * Herfindahl-Hirschman Index over shares, 0-10000. Above ~2500 is considered
 * highly concentrated — here it flags dependence on a single supplier.
 */
const hhi = (values) => {
  const total = values.reduce((a, b) => a + b, 0);
  if (!total) return 0;
  return Math.round(values.reduce((acc, v) => acc + Math.pow((v / total) * 100, 2), 0));
};

/** Net value of a PO line: quantity x (net price / price unit). */
const lineValue = (item) => {
  const priceQty = num(item.Netpricequantity) || 1;
  return num(item.Orderquantity) * (num(item.Netpriceamount) / priceQty);
};

// ---------- shared lookups ----------

/** Supplier code -> readable name, so charts never show a bare "100010". */
export const buildVendorNames = (vendorMaster) => {
  const map = {};
  vendorMaster.forEach((v) => {
    map[unpadId(v.Vendor)] = v.Name || unpadId(v.Vendor);
  });
  return map;
};

export const vendorLabel = (names, code) => names[unpadId(code)] || unpadId(code) || 'Unknown';

// ---------- Purchase Orders ----------

export const buildPoAnalytics = ({ poHeader, poItem, poSchedule, grItem, grHeader }, vendorNames) => {
  const totalValue = sum(poItem, lineValue);
  const headerBySupplier = groupBy(poHeader, (h) => unpadId(h.Supplier));

  // Spend per supplier, joined through the header (items carry no supplier).
  const supplierOfPo = {};
  poHeader.forEach((h) => { supplierOfPo[h.Purchaseorder] = unpadId(h.Supplier); });

  const spendBySupplier = Object.entries(
    poItem.reduce((acc, item) => {
      const supplier = supplierOfPo[item.Purchaseorder] || 'Unknown';
      acc[supplier] = (acc[supplier] || 0) + lineValue(item);
      return acc;
    }, {})
  ).map(([code, value]) => ({ name: vendorLabel(vendorNames, code), code, value }));

  const spendRanked = topN(spendBySupplier, 10);
  const supplierShares = spendBySupplier.map((s) => s.value);
  const topSupplier = spendRanked[0];

  // On-time delivery: GR posting date vs the PO schedule line's delivery date.
  const postingDate = {};
  grHeader.forEach((h) => { postingDate[h.MaterialDocument] = parseSapDate(h.PostingDate); });

  const dueDate = {};
  poSchedule.forEach((s) => {
    dueDate[itemKey(s.Purchasingdocument, s.Purchasingdocumentitem)] =
      parseSapDate(s.Schedulelinedeliverydate);
  });

  const delays = [];
  grItem.forEach((g) => {
    const due = dueDate[itemKey(g.PurchaseOrder, g.PurchaseOrderItem)];
    const posted = postingDate[g.MaterialDocument];
    if (due && posted) delays.push(daysBetween(posted, due));
  });

  const onTime = delays.filter((d) => d <= 0).length;
  const deliveryBuckets = [
    { name: 'Early / on time', count: delays.filter((d) => d <= 0).length },
    { name: '1-7 days late', count: delays.filter((d) => d > 0 && d <= 7).length },
    { name: '8-30 days late', count: delays.filter((d) => d > 7 && d <= 30).length },
    { name: '30+ days late', count: delays.filter((d) => d > 30).length },
  ];

  // Three-way match: received quantity per PO line vs ordered quantity.
  const receivedQty = {};
  grItem.forEach((g) => {
    const key = itemKey(g.PurchaseOrder, g.PurchaseOrderItem);
    receivedQty[key] = (receivedQty[key] || 0) + num(g.QuantityInEntryUnit);
  });

  let fullyReceived = 0, partial = 0, notReceived = 0, overReceived = 0;
  poItem.forEach((item) => {
    const received = receivedQty[itemKey(item.Purchaseorder, item.Purchaseorderitem)] || 0;
    const ordered = num(item.Orderquantity);
    if (received === 0) notReceived += 1;
    else if (received > ordered) overReceived += 1;
    else if (received >= ordered) fullyReceived += 1;
    else partial += 1;
  });

  // SAP's own completion flag vs what the quantities say. A gap means the flag
  // is stale, which is worth surfacing rather than silently trusting either.
  const flaggedComplete = poItem.filter((i) => i.Iscompletelydelivered === true).length;
  const derivedComplete = fullyReceived + overReceived;

  const values = poItem.map(lineValue).sort((a, b) => a - b);
  const median = values.length ? values[Math.floor(values.length / 2)] : 0;
  const top5Value = values.slice(-5).reduce((a, b) => a + b, 0);
  const top5Share = pct(top5Value, totalValue);

  const byPlant = Object.entries(groupBy(poItem, (i) => i.Plant || 'Unknown'))
    .map(([name, rows]) => ({ name, count: rows.length, value: sum(rows, lineValue) }));

  const creationTrend = Object.entries(groupBy(poHeader, (h) => toISODate(h.Creationdate)))
    .filter(([date]) => date)
    .map(([date, rows]) => ({ date, count: rows.length }))
    .sort((a, b) => a.date.localeCompare(b.date));

  return {
    kpis: {
      totalPos: poHeader.length,
      totalLines: poItem.length,
      totalValue,
      avgPoValue: poHeader.length ? totalValue / poHeader.length : 0,
      avgLineValue: poItem.length ? totalValue / poItem.length : 0,
      medianLineValue: median,
      linesPerPo: poHeader.length ? poItem.length / poHeader.length : 0,
      activeSuppliers: Object.keys(headerBySupplier).length,
      topSupplierName: topSupplier?.name || '—',
      topSupplierShare: topSupplier ? pct(topSupplier.value, totalValue) : null,
      top5LineShare: top5Share,
      supplierHhi: hhi(supplierShares),
      onTimeRate: delays.length ? pct(onTime, delays.length) : null,
      avgDelayDays: delays.length ? delays.reduce((a, b) => a + b, 0) / delays.length : null,
      worstDelayDays: delays.length ? Math.max(...delays) : null,
      fullyReceived,
      partial,
      notReceived,
      overReceived,
      receiptCoverage: poItem.length ? pct(derivedComplete, poItem.length) : null,
      flagMismatch: Math.abs(flaggedComplete - derivedComplete),
      pendingInvoice: poItem.filter((i) => i.Isfinallyinvoiced !== true).length,
      deletedLines: poItem.filter((i) => i.Purchasingdocumentdeletioncode).length,
    },
    charts: { spendBySupplier: spendRanked, deliveryBuckets, byPlant, creationTrend },
    tables: {
      matchStatus: [
        { status: 'Fully received', lines: fullyReceived },
        { status: 'Partially received', lines: partial },
        { status: 'Nothing received', lines: notReceived },
        { status: 'Over-received', lines: overReceived },
      ],
    },
  };
};

// ---------- Purchase Requisitions ----------

export const buildPrAnalytics = ({ prItem }) => {
  const totalValue = sum(prItem, (r) => r.ItemNetAmount);
  const converted = prItem.filter((r) => r.PurchasingDocument);
  const open = prItem.filter((r) => !r.PurchasingDocument);

  const byRequester = Object.entries(groupBy(prItem, (r) => r.CreatedByUser || 'Unknown'))
    .map(([name, rows]) => ({
      name,
      count: rows.length,
      value: sum(rows, (r) => r.ItemNetAmount),
    }));
  const requesterRanked = topN(byRequester, 10);

  const byMaterial = Object.entries(
    groupBy(prItem, (r) => r.PurchaseRequisitionItemText || 'Unspecified')
  ).map(([name, rows]) => ({ name, value: sum(rows, (r) => r.ItemNetAmount) }));

  const byPlant = Object.entries(groupBy(prItem, (r) => r.Plant || 'Unknown'))
    .map(([name, rows]) => ({ name, value: sum(rows, (r) => r.ItemNetAmount), count: rows.length }));

  // Age of still-unconverted requisitions, measured from creation to today.
  const now = new Date();
  const ages = open
    .map((r) => daysBetween(now, parseSapDate(r.CreationDate)))
    .filter((d) => d !== null);

  const agingBuckets = [
    { name: '0-7 days', count: ages.filter((d) => d <= 7).length },
    { name: '8-14 days', count: ages.filter((d) => d > 7 && d <= 14).length },
    { name: '15-30 days', count: ages.filter((d) => d > 14 && d <= 30).length },
    { name: '30+ days', count: ages.filter((d) => d > 30).length },
  ];

  const conversionValue = sum(converted, (r) => r.ItemNetAmount);
  const topRequester = requesterRanked[0];

  return {
    kpis: {
      totalPrs: prItem.length,
      totalValue,
      avgPrValue: prItem.length ? totalValue / prItem.length : 0,
      convertedCount: converted.length,
      openCount: open.length,
      conversionRate: prItem.length ? pct(converted.length, prItem.length) : null,
      conversionValueRate: totalValue ? pct(conversionValue, totalValue) : null,
      releasedPct: prItem.length
        ? pct(prItem.filter((r) => r.PurReqnReleaseStatus === '02').length, prItem.length)
        : null,
      avgAge: ages.length ? ages.reduce((a, b) => a + b, 0) / ages.length : null,
      oldestOpen: ages.length ? Math.max(...ages) : null,
      requesterCount: byRequester.length,
      topRequester: topRequester?.name || '—',
      topRequesterShare: topRequester ? pct(topRequester.value, totalValue) : null,
      requesterHhi: hhi(byRequester.map((r) => r.value)),
      closedCount: prItem.filter((r) => r.IsClosed === true).length,
    },
    charts: {
      byRequester: requesterRanked,
      byMaterial: topN(byMaterial, 10),
      byPlant,
      agingBuckets,
      conversion: [
        { name: 'Converted to PO', count: converted.length },
        { name: 'Still open', count: open.length },
      ],
    },
    tables: { byRequester: requesterRanked },
  };
};

// ---------- Goods Receipts ----------

export const buildGrAnalytics = ({ grHeader, grItem, poItem }, vendorNames) => {
  // GdsMvtExtAmtInCoCodeCrcy is present but zero on every row, so receipt value
  // has to be reconstructed from the PO price of the line being received.
  const priceOf = {};
  poItem.forEach((item) => {
    priceOf[itemKey(item.Purchaseorder, item.Purchaseorderitem)] = {
      net: num(item.Netpriceamount),
      priceQty: num(item.Netpricequantity) || 1,
    };
  });

  let receivedValue = 0;
  let unpricedLines = 0;
  grItem.forEach((g) => {
    const price = priceOf[itemKey(g.PurchaseOrder, g.PurchaseOrderItem)];
    if (!price) { unpricedLines += 1; return; }
    receivedValue += num(g.QuantityInEntryUnit) * (price.net / price.priceQty);
  });

  const totalQty = sum(grItem, (g) => g.QuantityInEntryUnit);

  const bySupplier = Object.entries(groupBy(grItem, (g) => unpadId(g.Supplier)))
    .map(([code, rows]) => ({
      name: vendorLabel(vendorNames, code),
      value: sum(rows, (r) => r.QuantityInEntryUnit),
      count: rows.length,
    }));

  const byMaterial = Object.entries(groupBy(grItem, (g) => g.Material || 'Unknown'))
    .map(([name, rows]) => ({ name, value: sum(rows, (r) => r.QuantityInEntryUnit) }));

  const byPlant = Object.entries(groupBy(grItem, (g) => g.Plant || 'Unknown'))
    .map(([name, rows]) => ({ name, value: sum(rows, (r) => r.QuantityInEntryUnit) }));

  const movementTypes = Object.entries(groupBy(grItem, (g) => g.GoodsMovementType || '—'))
    .map(([name, rows]) => ({ name, count: rows.length }));

  const trend = Object.entries(groupBy(grHeader, (h) => toISODate(h.PostingDate)))
    .filter(([date]) => date)
    .map(([date, rows]) => ({ date, count: rows.length }))
    .sort((a, b) => a.date.localeCompare(b.date));

  const postingDates = grHeader
    .map((h) => parseSapDate(h.PostingDate))
    .filter(Boolean)
    .sort((a, b) => a - b);

  const recent = [...grItem]
    .slice(0, 200)
    .map((g) => ({
      document: g.MaterialDocument,
      material: g.Material,
      supplier: vendorLabel(vendorNames, g.Supplier),
      po: g.PurchaseOrder,
      plant: g.Plant,
      qty: num(g.QuantityInEntryUnit),
    }));

  return {
    kpis: {
      totalGrns: grHeader.length,
      totalLines: grItem.length,
      totalQty,
      receivedValue,
      avgQtyPerLine: grItem.length ? totalQty / grItem.length : 0,
      distinctMaterials: new Set(grItem.map((g) => g.Material)).size,
      distinctPlants: new Set(grItem.map((g) => g.Plant)).size,
      distinctPos: new Set(grItem.map((g) => g.PurchaseOrder).filter(Boolean)).size,
      withPoLink: grItem.filter((g) => g.PurchaseOrder).length,
      cancelled: grItem.filter((g) => g.GoodsMovementIsCancelled === true).length,
      unpricedLines,
      mostRecent: postingDates.length
        ? toISODate(postingDates[postingDates.length - 1])
        : '—',
      supplierCount: bySupplier.length,
    },
    charts: {
      bySupplier: topN(bySupplier, 10),
      byMaterial: topN(byMaterial, 10),
      byPlant,
      movementTypes,
      trend,
    },
    tables: { recent, bySupplier: topN(bySupplier, 10) },
  };
};

// ---------- Vendor Invoices / Payables ----------

export const buildApAnalytics = ({ invoice, vendorOpen }, vendorNames) => {
  // Only account type "K" is the vendor line. Summing every BSEG row mixes in
  // "WE" goods-receipt clearing entries and massively overstates the total —
  // that is the bug in the FastAPI /ap/data endpoint.
  const vendorLines = vendorOpen.filter((r) => r.Accounttype === 'K');
  const openLines = vendorLines.filter((r) => !parseSapDate(r.Clearingdate));
  const clearedLines = vendorLines.filter((r) => parseSapDate(r.Clearingdate));

  const invoiceTotal = sum(invoice, (r) => r.InvoiceGrossAmount);
  const paidTotal = sum(clearedLines, (r) => r.Amountlocalcurrency);
  const outstanding = sum(openLines, (r) => r.Amountlocalcurrency);

  const now = new Date();
  const bucketDefs = [
    { name: 'Not yet due', test: (d) => d < 0 },
    { name: '0-30 days', test: (d) => d >= 0 && d <= 30 },
    { name: '31-60 days', test: (d) => d > 30 && d <= 60 },
    { name: '61-90 days', test: (d) => d > 60 && d <= 90 },
    { name: '90+ days', test: (d) => d > 90 },
  ];

  const aged = invoice
    .map((inv) => {
      const due = parseSapDate(inv.DueCalculationBaseDate);
      return due
        ? { age: daysBetween(now, due), amount: num(inv.InvoiceGrossAmount), invoice: inv }
        : null;
    })
    .filter(Boolean);

  const agingBuckets = bucketDefs.map((b) => {
    const rows = aged.filter((a) => b.test(a.age));
    return {
      name: b.name,
      count: rows.length,
      value: rows.reduce((acc, r) => acc + r.amount, 0),
    };
  });

  const overdue = aged.filter((a) => a.age > 0);

  const byVendor = Object.entries(groupBy(invoice, (r) => unpadId(r.InvoicingParty)))
    .map(([code, rows]) => ({
      name: vendorLabel(vendorNames, code),
      code,
      value: sum(rows, (r) => r.InvoiceGrossAmount),
      count: rows.length,
    }));
  const vendorRanked = topN(byVendor, 10);

  const outstandingByVendor = Object.entries(groupBy(openLines, (r) => unpadId(r.Vendornumber)))
    .map(([code, rows]) => ({
      name: vendorLabel(vendorNames, code),
      value: sum(rows, (r) => r.Amountlocalcurrency),
      count: rows.length,
    }));

  const clearDays = clearedLines
    .map((r) => daysBetween(parseSapDate(r.Clearingdate), parseSapDate(r.Postingdate)))
    .filter((d) => d !== null);

  const topVendor = vendorRanked[0];

  return {
    kpis: {
      invoiceCount: invoice.length,
      invoiceTotal,
      paidTotal,
      outstanding,
      openLineCount: openLines.length,
      clearedLineCount: clearedLines.length,
      paymentRatio: invoiceTotal ? pct(paidTotal, invoiceTotal) : null,
      vendorCount: byVendor.length,
      topVendor: topVendor?.name || '—',
      topVendorShare: topVendor ? pct(topVendor.value, invoiceTotal) : null,
      vendorHhi: hhi(byVendor.map((v) => v.value)),
      overdueCount: overdue.length,
      overdueValue: overdue.reduce((acc, r) => acc + r.amount, 0),
      avgInvoiceValue: invoice.length ? invoiceTotal / invoice.length : 0,
      avgDaysToClear: clearDays.length
        ? clearDays.reduce((a, b) => a + b, 0) / clearDays.length
        : null,
      creditMemos: invoice.filter((r) => r.SupplierInvoiceIsCreditMemo === true).length,
      reversed: invoice.filter((r) => r.IsReversed === true).length,
    },
    charts: { agingBuckets, byVendor: vendorRanked, outstandingByVendor },
    tables: {
      vendorSummary: vendorRanked.map((v) => {
        const open = outstandingByVendor.find((o) => o.name === v.name);
        return {
          vendor: v.name,
          invoices: v.count,
          invoiced: v.value,
          outstanding: open?.value || 0,
        };
      }),
    },
  };
};

// ---------- Cross-cycle overview ----------

export const buildOverview = (po, pr, gr, ap, raw) => {
  const funnel = [
    { stage: 'Requisitions', count: pr.kpis.totalPrs },
    { stage: 'Purchase Orders', count: po.kpis.totalLines },
    { stage: 'Goods Receipts', count: gr.kpis.totalLines },
    { stage: 'Invoices', count: ap.kpis.invoiceCount },
  ];

  const valueFlow = [
    { stage: 'Requisitioned', value: pr.kpis.totalValue },
    { stage: 'Ordered', value: po.kpis.totalValue },
    { stage: 'Received', value: gr.kpis.receivedValue },
    { stage: 'Invoiced', value: ap.kpis.invoiceTotal },
    { stage: 'Paid', value: ap.kpis.paidTotal },
  ];

  // Leakage between consecutive stages of the cycle.
  const prToPo = pr.kpis.totalValue
    ? pct(po.kpis.totalValue - pr.kpis.totalValue, pr.kpis.totalValue)
    : null;
  const poToGr = po.kpis.totalValue ? pct(gr.kpis.receivedValue, po.kpis.totalValue) : null;
  const grToInvoice = gr.kpis.receivedValue
    ? pct(ap.kpis.invoiceTotal, gr.kpis.receivedValue)
    : null;

  // Data-quality signals worth showing rather than hiding.
  const dataQuality = [
    {
      label: 'Delivery flag vs quantities',
      detail: `${po.kpis.flagMismatch} PO line(s) where SAP's completion flag disagrees with received quantity`,
      severity: po.kpis.flagMismatch > 0 ? 'warn' : 'ok',
    },
    {
      label: 'GR lines without a PO price',
      detail: `${gr.kpis.unpricedLines} receipt line(s) could not be valued`,
      severity: gr.kpis.unpricedLines > 0 ? 'warn' : 'ok',
    },
    {
      label: 'Receipts not yet invoiced',
      detail: `${po.kpis.pendingInvoice} PO line(s) still open for invoicing`,
      severity: po.kpis.pendingInvoice > 0 ? 'info' : 'ok',
    },
    {
      label: 'Overdue invoices',
      detail: `${ap.kpis.overdueCount} invoice(s) past their due date`,
      severity: ap.kpis.overdueCount > 0 ? 'warn' : 'ok',
    },
  ];

  return {
    kpis: {
      prValue: pr.kpis.totalValue,
      poValue: po.kpis.totalValue,
      receivedValue: gr.kpis.receivedValue,
      invoicedValue: ap.kpis.invoiceTotal,
      outstanding: ap.kpis.outstanding,
      prToPoValueDelta: prToPo,
      poToGrRate: poToGr,
      grToInvoiceRate: grToInvoice,
      conversionRate: pr.kpis.conversionRate,
      onTimeRate: po.kpis.onTimeRate,
      receiptCoverage: po.kpis.receiptCoverage,
      supplierHhi: po.kpis.supplierHhi,
      activeSuppliers: po.kpis.activeSuppliers,
      openPrs: pr.kpis.openCount,
      pendingInvoice: po.kpis.pendingInvoice,
      overdueCount: ap.kpis.overdueCount,
      topSupplierName: po.kpis.topSupplierName,
      topSupplierShare: po.kpis.topSupplierShare,
    },
    charts: {
      funnel,
      valueFlow,
      spendBySupplier: po.charts.spendBySupplier,
      deliveryBuckets: po.charts.deliveryBuckets,
      agingBuckets: ap.charts.agingBuckets,
      conversion: pr.charts.conversion,
    },
    dataQuality,
    counts: {
      poHeader: raw.poHeader.length,
      poItem: raw.poItem.length,
      prItem: raw.prItem.length,
      grItem: raw.grItem.length,
      invoice: raw.invoice.length,
    },
  };
};

/** Single entry point: raw entity sets in, everything the dashboard renders out. */
export const buildAllAnalytics = (raw) => {
  const vendorNames = buildVendorNames(raw.vendorMaster || []);
  const po = buildPoAnalytics(raw, vendorNames);
  const pr = buildPrAnalytics(raw);
  const gr = buildGrAnalytics(raw, vendorNames);
  const ap = buildApAnalytics(raw, vendorNames);
  const overview = buildOverview(po, pr, gr, ap, raw);
  return { overview, po, pr, gr, ap, vendorNames };
};
